import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Gift, CheckCircle, Phone, ExternalLink, GraduationCap, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { COMPANY_INFO } from '../lib/utils';
import { SEOHead, breadcrumbSchema, faqSchema } from '../components/SEOHead';
import { VIEWPORT_ONCE, SCALE_IN_INITIAL, SCALE_IN_ANIMATE, SCALE_IN_TRANSITION_SLOW } from '../lib/motion';
import {
  CHOUETTE_PATH, CHOUETTE_SITE, CHOUETTE_ICON, CHOUETTE_SCREENSHOTS, CHOUETTE_PILLARS,
  CHOUETTE_ACTIVITIES, CHOUETTE_LEVELS, CHOUETTE_PARENTS, CHOUETTE_STEPS, CHOUETTE_FAQ,
} from '../lib/chouette-data';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.2, 0.8, 0.2, 1] } }
};
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const SECTION_BADGE = "mb-4 px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90";
const H2 = "font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-3";

function SectionHeader({ badge, title, text }) {
  return (
    <motion.div className="text-center mb-12" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
      <Badge className={SECTION_BADGE}>{badge}</Badge>
      <h2 className={H2}>{title}</h2>
      {text && <p className="text-muted-foreground max-w-2xl mx-auto">{text}</p>}
    </motion.div>
  );
}

export default function ChouetteApprentissagePage() {
  const schema = [
    breadcrumbSchema([{ name: 'Accueil', path: '/' }, { name: 'Chouette Apprentissage offert', path: CHOUETTE_PATH }]),
    faqSchema(CHOUETTE_FAQ),
  ];

  return (
    <div data-testid="chouette-page">
      <SEOHead
        title="Chouette Apprentissage offert — Pack Famille pour nos clients"
        description="Clients Ptitservices avec un abonnement actif : le Pack Famille de l'application Chouette Apprentissage (aide aux devoirs du CP à la Terminale, 5 €/mois) vous est offert automatiquement. Signalez-vous comme client dans l'application, c'est tout."
        path={CHOUETTE_PATH}
        image={CHOUETTE_ICON}
        schema={schema}
      />

      {/* ====== HERO ====== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,hsla(195,20%,97%,1)_0%,hsla(182,40%,94%,1)_55%,hsla(195,20%,97%,1)_100%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20">
          <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-foreground">Accueil</Link>
            <span>/</span>
            <span className="text-foreground font-medium">Chouette Apprentissage offert</span>
          </nav>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div className="lg:col-span-7" initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-2 mb-4">
                <Badge className="px-5 py-2 text-base font-semibold rounded-full bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90">
                  <Gift className="h-4 w-4 mr-2" /> Avantage client
                </Badge>
                <Badge variant="outline" className="px-4 py-2 text-sm rounded-full bg-card">Valeur 5,00 € / mois</Badge>
              </motion.div>
              <motion.h1 variants={fadeUp} className="font-['Space_Grotesk'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight mb-4" data-testid="chouette-hero-title">
                L'aide aux devoirs de vos enfants,{' '}
                <span className="text-[hsl(var(--primary))]">offerte avec votre abonnement</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-base sm:text-lg text-muted-foreground leading-relaxed mb-6 max-w-xl">
                Nous offrons le <strong>Pack Famille</strong> de l'application <strong>Chouette Apprentissage</strong> à tous nos clients disposant d'un abonnement actif. Un tuteur bienveillant qui accompagne vos enfants du <strong>CP à la Terminale</strong> — sans jamais donner la réponse, en la faisant trouver.
              </motion.p>
              <motion.ul variants={fadeUp} className="grid sm:grid-cols-2 gap-2 mb-8 text-sm">
                {['Pack Famille : 2 enfants et +', 'Toutes les matières, CP → Terminale', 'Activation automatique dans l\'application', '0 € tant que votre abonnement est actif'].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-[hsl(var(--primary))] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </motion.ul>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href={CHOUETTE_SITE} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg" data-testid="chouette-hero-claim-button">
                    Je suis client : découvrir l'application
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
                <Link to="/devis">
                  <Button size="lg" variant="outline" data-testid="chouette-hero-quote-button">
                    Pas encore client ? Devis gratuit
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            <motion.div className="lg:col-span-5" initial={SCALE_IN_INITIAL} animate={SCALE_IN_ANIMATE} transition={SCALE_IN_TRANSITION_SLOW}>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-card border border-border p-6">
                <div className="flex items-center gap-4 mb-5">
                  <img src={CHOUETTE_ICON} alt="Icône de l'application Chouette Apprentissage" className="w-16 h-16 rounded-2xl shadow-md" loading="eager" />
                  <div>
                    <span className="font-['Space_Grotesk'] font-semibold text-lg block">Chouette Apprentissage</span>
                    <span className="text-xs text-muted-foreground">Les devoirs deviennent chouettes · CP → Terminale</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div className="rounded-xl border border-border p-4 bg-[hsl(var(--secondary))]/60">
                    <span className="text-xs text-muted-foreground block">Solo · 1 enfant</span>
                    <span className="text-xl font-bold">3,00 €<span className="text-xs font-normal text-muted-foreground">/mois</span></span>
                  </div>
                  <div className="rounded-xl border-2 border-[hsl(var(--primary))] p-4 bg-[hsl(var(--brand-mint))]/30 relative">
                    <Badge className="absolute -top-2.5 right-3 bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))] text-[10px] px-2">Offert</Badge>
                    <span className="text-xs text-muted-foreground block">Pack Famille · 2 enfants et +</span>
                    <span className="text-sm line-through text-muted-foreground mr-2">5,00 €</span>
                    <span className="text-xl font-bold text-[hsl(var(--primary))]" data-testid="chouette-offer-price">0 €<span className="text-xs font-normal text-muted-foreground">/mois</span></span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Activation automatique : signalez-vous comme client Ptitservices.com dans l'application. Disponible prochainement sur l'App Store et Google Play.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== L'OFFRE ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60" data-testid="chouette-offer-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionHeader badge="Comment en profiter ?" title="Le Pack Famille offert en 3 étapes" text="Un avantage exclusif réservé à nos clients abonnés, activé automatiquement dans l'application, sans aucune démarche auprès de l'agence." />
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {CHOUETTE_STEPS.map((step) => (
              <motion.div key={step.num} variants={fadeUp}>
                <Card className="p-6 h-full bg-card border border-border shadow-sm" data-testid="chouette-step-card">
                  <span className="font-['Space_Grotesk'] text-3xl font-bold text-[hsl(var(--primary))]/20">{step.num}</span>
                  <h3 className="font-['Space_Grotesk'] font-semibold mt-2 mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <div className="text-center mt-8 flex flex-wrap justify-center gap-3">
            <a href={CHOUETTE_SITE} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" data-testid="chouette-app-button">
                Accéder à Chouette Apprentissage <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/devis">
              <Button size="lg" variant="outline" data-testid="chouette-support-button">Pas encore client ? Devis gratuit <ArrowRight className="ml-2 h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ====== LE COMPAGNON ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionHeader badge="Le compagnon virtuel" title="Ici, on ne donne jamais la réponse. On la fait trouver." text="Un compagnon animé qui pose les bonnes questions, repère les lacunes et guide l'élève jusqu'au « eurêka »." />
          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {CHOUETTE_PILLARS.map((p) => (
              <motion.div key={p.title} variants={fadeUp}>
                <Card className="p-6 h-full border border-border shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center">
                      <p.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                    </div>
                    <h3 className="font-['Space_Grotesk'] font-semibold">{p.title}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== ACTIVITÉS ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionHeader badge="Les activités" title="Des activités pour toutes les mémoires" text="Leçons, jeux, dictées, débats, tracés : chaque notion revient sous plusieurs formes, pour entrer dans la tête de chaque enfant." />
          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {CHOUETTE_ACTIVITIES.map((a) => (
              <motion.div key={a.title} variants={fadeUp}>
                <Card className="p-6 h-full bg-card border border-border shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center mb-3">
                    <a.icon className="h-5 w-5 text-[hsl(var(--primary))]" />
                  </div>
                  <h3 className="font-['Space_Grotesk'] font-semibold mb-1">{a.title}</h3>
                  <p className="text-sm text-muted-foreground">{a.desc}</p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== NIVEAUX ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionHeader badge="Le parcours" title="Un compagnon qui grandit avec eux" text="Seules les classes proches du niveau de l'enfant lui sont proposées, du CP à la Terminale, conformément au programme de l'Éducation nationale." />
          <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger}>
            {CHOUETTE_LEVELS.map((lvl) => (
              <motion.div key={lvl.title} variants={fadeUp}>
                <Card className="overflow-hidden h-full border border-border shadow-sm">
                  <img src={lvl.img} alt={lvl.alt} className="w-full h-[170px] object-cover" loading="lazy" />
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-1">
                      <GraduationCap className="h-4 w-4 text-[hsl(var(--primary))]" />
                      <h3 className="font-['Space_Grotesk'] font-semibold">{lvl.title}</h3>
                    </div>
                    <p className="text-xs font-medium text-[hsl(var(--primary))] mb-2">{lvl.classes}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{lvl.desc}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ====== ESPACE PARENTS ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
              <Badge className={SECTION_BADGE}>Pour les parents</Badge>
              <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Un espace parents complet et sécurisé</h2>
              <ul className="space-y-3">
                {CHOUETTE_PARENTS.map((item) => (
                  <li key={item.text} className="flex items-start gap-3 text-sm">
                    <item.icon className="h-5 w-5 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground italic mt-4">Sécurité renforcée : e-mail confirmé et identité vérifiée (pièce d'identité + selfie) avant toute fonction sociale.</p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} className="flex justify-center">
              <img src={CHOUETTE_SCREENSHOTS[5].src} alt="Écran de l'espace parents de l'application Chouette Apprentissage" className="max-h-[520px] w-auto rounded-2xl shadow-2xl border border-border" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== CAPTURES ====== */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <SectionHeader badge="En images" title="L'application en vrai" text="Six écrans, une promesse : comprendre vraiment — et réviser en toute sécurité." />
          <motion.div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} data-testid="chouette-screenshots">
            {CHOUETTE_SCREENSHOTS.map((shot) => (
              <motion.figure key={shot.label} variants={fadeUp} className="text-center">
                <img src={shot.src} alt={`Écran ${shot.label} de l'application Chouette Apprentissage`} className="w-full rounded-xl shadow-md border border-border" loading="lazy" />
                <figcaption className="text-xs text-muted-foreground mt-2">{shot.label}</figcaption>
              </motion.figure>
            ))}
          </motion.div>
          <div className="text-center mt-8">
            <a href={CHOUETTE_SITE} target="_blank" rel="noopener noreferrer" data-testid="chouette-external-link">
              <Button variant="outline">Découvrir chouetteapprentissage.com <ExternalLink className="ml-2 h-4 w-4" /></Button>
            </a>
          </div>
        </div>
      </section>

      {/* ====== FAQ ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--secondary))]/60">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <SectionHeader badge="FAQ" title="Questions fréquentes sur l'offre" />
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp} data-testid="chouette-faq">
            <Accordion type="single" collapsible className="space-y-3">
              {CHOUETTE_FAQ.map((item, i) => (
                <AccordionItem key={item.q} value={`faq-${i}`} className="border border-border rounded-lg px-4 bg-card">
                  <AccordionTrigger className="text-sm font-medium text-left hover:no-underline">{item.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* ====== FINAL CTA ====== */}
      <section className="py-12 sm:py-16 lg:py-20 bg-[hsl(var(--primary))] text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={fadeUp}>
            <Sparkles className="h-8 w-8 mx-auto mb-4 opacity-90" />
            <h2 className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-semibold tracking-tight mb-4">Un domicile facilité, des devoirs plus sereins</h2>
            <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
              Ménage à partir de 14,99 €/h, jardinage à partir de 24,95 €/h après avance immédiate — et le Pack Famille Chouette Apprentissage offert avec votre abonnement.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/devis">
                <Button size="lg" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white shadow-lg" data-testid="chouette-final-quote-button">
                  Demander un devis gratuit
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <a href={`tel:${COMPANY_INFO.phoneRaw}`}>
                <Button size="lg" variant="secondary">
                  <Phone className="mr-2 h-4 w-4" />
                  {COMPANY_INFO.phone}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
